<template>
  <div>
    <b-card
      v-can="'add-confirmed'"
    >
      <b-row class="mb-1">
        <b-col>
          <form-builder
            ref="formbuilder"
            v-model="formData"
            :on-submit="onSubmit"
            grid-class="px-1 row"
            :form-structure="formStructure"
          >
            <template slot="buttons">
              <b-col
                cols="12"
                md="3"
                class="d-flex mobile-width-align"
              >
                <b-button
                  type="submit"
                  class="vh-5 mt-2 ml-1 ml-md-1 custom-button-color"
                  style="height: 37px;"
                  :disabled="!(formData.waybill_number) || loadingResponse"
                >
                  {{ loadingResponse ? 'Submitting...' : 'Submit' }}
                </b-button>
              </b-col>
            </template>
          </form-builder>
        </b-col>
      </b-row>
    </b-card>

    <b-card-actions
      title="Filters"
      action-collapse
      collapsed
    >
      <b-row class="mb-0">
        <b-col
          md="4"
          sm="2"
        >
          <b-form-group
            label="Created Date"
            label-for="basicInput"
          >
            <div class="position-relative">
              <AdvancedDateFilter
                :date-value="filters['orders.created_at']"
                :opens="'right'"
                @update="updateDateRange('orders.created_at', $event)"
              />
            </div>
          </b-form-group>
        </b-col>
      </b-row>
      <!-- Apply and Clear Filters -->
      <b-row class="mt-1">
        <b-col>
          <filter-buttons
            :filter-data="filters"
            :apply-filters="applyFilters"
            :clear-filters="clearFilters"
          />
        </b-col>
      </b-row>
    </b-card-actions>

    <b-card no-body>
      <b-row class="mb-1 mr-1 ml-1">
        <b-col
          class="d-flex justify-content-start align-items-center"
          md="4"
        >
          <bulk-submit
            v-if="$can('bulk-update-operation') && $can('add-confirmed')"
            :status-key="status_key"
            :selected-waybills="selectedWaybillArray"
            @bulkSubmit="onClickBulkAction"
          />
        </b-col>
        <b-col
          class="d-flex justify-content-between align-items-center mb-1 mb-md-0"
          md="8"
        >
          <b-button
            type="submit"
            class="mt-2 ml-0 ml-md-3 custom-button-color-blur"
            :disabled="false"
          >
            Scanned Orders : {{ submitCount }}
          </b-button>
          <TableButtons
            class="ml-2 mt-2"
            :items="rows"
            :json_fieldz="json_fields"
            :name="`Confirmed - Page ${page}.xls`"
            :bulkname="`Confirmed.xls`"
            :fetch="getOrderListNoPagination"
            @refresh="onClickRefresh"
          />
        </b-col>
      </b-row>
      <b-row class="mb-1 mr-1 ml-1">
        <b-col
          class="d-flex justify-content-start align-items-center"
          md="6"
        >
          <div class="font-weight-bolder">
            {{ `${selectedWaybillArray.length} record${selectedWaybillArray.length === 1 ? '':'s'}` }} selected
          </div>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-overlay
            id="overlay-background"
            blur="10px"
            opacity="0.30"
            rounded="sm"
            variant="light"
          >
            <b-table
              :per-page="perPage"
              :fields="columns"
              :items="rows"
              hover
              responsive
              striped
              show-empty
              sticky-header="100vh"
              class="hide-vertical-scroll"
            >
              <template #empty>
                <TableDataFetching
                  :rows="rows"
                  :data-loading="dataLoading"
                />
              </template>
              <template #head(id)>
                <div class="d-flex align-items-start">
                  <div>
                    <b-form-checkbox
                      id="selectAllCheckbox"
                      v-model="selectAllCheckbox"
                      name="selectAllCheckbox"
                      @change="onClickSelectAllCheckbox()"
                    />
                  </div>
                </div>
              </template>
              <template #cell(id)="data">
                <div class="d-flex align-items-start">
                  <div>
                    <div class="font-weight-bolder">
                      <b-form-checkbox
                        v-model="selectedWaybillArray"
                        :value="data.item.waybill_number"
                      />
                    </div>
                  </div>
                </div>
              </template>
              <template #cell(waybill_number)="data">
                <div class="d-flex align-items-center">
                  <b-avatar
                    :text="avatarText(getCustomerFieldValue(data.item, 'customer_name', data.item.waybill_number))"
                    size="32"
                    variant="light-primary"
                  />
                  <div>
                    <div
                      class="font-weight-bolder ml-1 text-primary"
                      @click="onClickViewOrder(data.item.id)"
                    >
                      {{ data.value }}
                    </div>
                    <div class="font-small-2 ml-1">
                      {{ __dateTimeFormatter(data.item.created_at) }}
                    </div>
                  </div>
                </div>
              </template>
              <template #cell(customer_email)="data">
                <div class="d-flex align-items-center">
                  <div>
                    <div class="font-weight-bolder ml-1">
                      {{ getCustomerFieldValue(data.item, 'customer_name') }}
                    </div>
                    <div class="font-small-2 ml-1">
                      {{ getCustomerFieldValue(data.item, 'customer_email', data.value) }}
                    </div>
                    <div class="font-small-2  ml-1">
                      <div><b>Primary number: </b>{{ getCustomerFieldValue(data.item, 'customer_phone') }}</div>
                    </div>
                    <div
                      v-if="shouldShowSecondaryPhone(data.item)"
                      class="font-small-2  ml-1"
                    >
                      <div><b>Second number: </b>{{ getCustomerFieldValue(data.item, 'customer_secondary_phone') }}</div>
                    </div>
                    <div class="font-small-2 ml-1">
                      {{ getCustomerFieldValue(data.item, 'customer_address') }}
                    </div>
                  </div>
                </div>
              </template>
              <template #cell(origin_warehouse)="data">
                <div class="d-flex align-items-center">
                  <div>
                    <div class="font-weight-bolder ml-1">
                      CITY - {{ data.item.origin_city.name }}
                    </div>
                    <div class="font-small-2 ml-1">
                      Warehouse - {{ data.value.name || 'N/A' }}
                    </div>
                  </div>
                </div>
              </template>

              <template #cell(destination_warehouse)="data">
                <div class="d-flex align-items-center">
                  <div>
                    <div class="font-weight-bolder ml-1">
                      CITY - {{ data.item.destination_city.name }}
                      <!--                    </b-badge>-->
                    </div>
                    <div class="font-small-2 ml-1">
                      Warehouse - {{ data.value.name || 'N/A' }}
                    </div>
                  </div>
                </div>
              </template>
              <template #cell(created_at)="data">
                {{ __dateTimeFormatter(data.value) }}
              </template>
              <template #cell(status.key)="data">
                <order-status-badge :status-key="data.value" />
              </template>
              <template #cell(cod)="data">
                {{ __numberWithCommas(data.value, true) }}
              </template>
              <template #cell(collected_cod)="data">
                {{ __numberWithCommas(data.value, true) }}
              </template>
              <template #cell(delivery_charge)="data">
                {{ __numberWithCommas(data.value, true) }}
              </template>
              <template #cell(weight)="data">
                {{ (data.value).toLocaleString() }}
              </template>
              <template #cell(action)="data">
                <b-dropdown
                  variant="link"
                  toggle-class="text-decoration-none"
                  no-caret
                >
                  <template v-slot:button-content>
                    <feather-icon
                      icon="MoreVerticalIcon"
                      size="12"
                      class="text-body align-middle mr-25"
                    />
                  </template>
                  <b-dropdown-item @click="onClickViewOrder(data.item.id)">
                    <feather-icon icon="EyeIcon" />
                    View Details
                  </b-dropdown-item>
                </b-dropdown>
              </template>
            </b-table>
          </b-overlay>

          <div class="mx-2 mb-2">
            <b-row>
              <b-col
                class="d-flex align-items-center justify-content-center justify-content-sm-start"
                md="3"
              >
                <label class="width-75">Per page</label>
                <v-select
                  v-model="perPage"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="pageOptions"
                  :clearable="false"
                  class="per-page-selector d-inline-block width-125 mr-1"
                />
              </b-col>
              <!-- Pagination -->
              <b-col
                class="d-flex align-items-center justify-content-sm-end"
                md="9"
              >
                <span class="text-muted mr-1 pagination-text mt-1 mt-md-0">Showing {{ meta.from }} to {{ meta.to }} of
                  {{ meta.total }} entries</span>
                <b-pagination
                  v-model="page"
                  :per-page="perPage"
                  :total-rows="(meta.total)? meta.total : 0"
                  class="mb-0 mt-1 mt-sm-0"
                  first-number
                  last-number
                  next-class="next-item"
                  prev-class="prev-item"
                >
                  <template #prev-text>
                    <feather-icon
                      icon="ChevronLeftIcon"
                      size="18"
                    />
                  </template>
                  <template #next-text>
                    <feather-icon
                      icon="ChevronRightIcon"
                      size="18"
                    />
                  </template>
                </b-pagination>
              </b-col>
            </b-row>
          </div>
        </b-col>
      </b-row>
    </b-card>
    <view-order-modal ref="viewOrderModal" />

    <bulk-action-modal
      ref="bulkActionModal"
      :form_structure="formStructure"
      :status_key="status_key"
      :selected_waybill_array="selectedWaybillArray"
    />
  </div>
</template>

<script>
import {
  BAvatar,
  BRow,
  BCol,
  BTable,
  BCard,
  BButton,
  BFormGroup,
  BPagination,
  BOverlay,
  BFormCheckbox,
  BDropdown,
  BDropdownItem,
  VBTooltip,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import AdvancedDateFilter from '@/components/AdvancedDateFilter/AdvancedDateFilter.vue'
import Filter from '@core/mixins/filter'
import SuccessMessage from '@core/mixins/SuccessMessage'
import OrderStatusBadge from '@core/components/status/OrderStatusBadge.vue'
import vSelect from 'vue-select'
import displayAmountWithCurrency from '@core/mixins/currency'
import ErrorMessage from '@core/mixins/ErrorMessage'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import { avatarText } from '@core/utils/filter'
import FormBuilder from '@/libs/builders/form-builder/FormBuilder.vue'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import ViewOrderModal from '@/components/orders/ViewOrderModal.vue'
import FilterButtons from '@/components/Filter/FilterButtons.vue'
import BulkActionModal from './bulk-actions/BulkActionModal.vue'
import BulkSubmit from './bulk-actions/BulkSubmit.vue'

const orderRepository = RepositoryFactory.get('order')

export default {
  name: 'KeyTwo',
  directives: {
    'b-tooltip': VBTooltip,
    Ripple,
  },
  components: {
    BAvatar,
    AdvancedDateFilter,
    BRow,
    BCol,
    BTable,
    BCard,
    FormBuilder,
    BButton,
    BFormGroup,
    BPagination,
    BOverlay,
    BulkActionModal,
    BFormCheckbox,
    BCardActions,
    vSelect,
    OrderStatusBadge,
    BDropdown,
    BDropdownItem,
    ViewOrderModal,
    FilterButtons,
    BulkSubmit,
  },
  mixins: [Filter, SuccessMessage, ErrorMessage, displayAmountWithCurrency],
  props: {
    // eslint-disable-next-line vue/prop-name-casing
    status_key: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      avatarText,
      submitCount: 0,
      dataLoading: false,
      rows: [],
      columns: [],
      json_fields: {
        'Waybill Number': 'waybill_number',
        'Origin City': {
          field: 'origin_city.name',
          callback: value => `"${value}"`,
        },
        'Origin Warehouse': {
          field: 'origin_warehouse.name',
          callback: value => `"${value}"`,
        },
        'Destination City': {
          field: 'destination_city.name',
          callback: value => `"${value}"`,
        },
        'Destination Warehouse': {
          field: 'destination_warehouse.name',
          callback: value => `"${value}"`,
        },
        'Customer Name': {
          field: 'customer_name',
          callback: value => `"${value}"`,
        },
        'Customer Email': {
          field: 'customer_email',
          callback: value => `"${value}"`,
        },
        'Customer Address': {
          field: 'customer_address',
          callback: value => `"${value}"`, // Wrap the address in double quotes
        },
        'Customer Phone': 'customer_phone',
        'Second Phone Number': 'customer_secondary_phone',
        COD: {
          field: 'cod',
          callback: value => `"${value}"`,
        },
        'Delivery Charge': {
          field: 'delivery_charge',
          callback: value => `"${value}"`,
        },
        'Collected Cod': 'collected_cod',
        Weight: 'weight',
        Status: 'status.name',
      },
      perPage: 20,
      pageOptions: [10, 20, 50],
      filters: {
        'orders.created_at': {
          startDate: null,
          endDate: null,
        },
      },
      total: 0,
      loadingResponse: false,
      meta: {},
      page: 1,
      formData: {},
      formStructure: [
        {
          type: 'input',
          placeholder: '',
          inputType: 'text',
          title: 'Waybill Number',
          value: 'waybill_number',
          class: 'col-12 col-md-3',
          validation: 'required',
        },
      ],
      selectedWaybillArray: [],
      selectAllCheckbox: false,
    }
  },
  watch: {
    page() {
      this.getOrderList()
    },
    perPage() {
      this.getOrderList()
    },
    // eslint-disable-next-line no-unused-vars,func-names
    'formData.waybill_number': function (newValue, oldValue) {
      const waybill = newValue
      if (waybill.length >= 8) {
        // this.getOrderDetails(waybill)
      }
    },
    loadingResponse(newValue) {
      /* eslint no-underscore-dangle: 0 */
      this.__toggleDisableInput('Waybill Number', newValue)
    },
  },
  async mounted() {
    this.getOrderList()
    this.columns.push(
      {
        key: 'id',
        thClass: 'text-left',
        tdClass: 'text-left',
      },
      {
        key: 'waybill_number',
        label: 'WayBill Number',
        sortable: true,
      },
      {
        key: 'customer_name',
        label: 'Customer Name',
        sortable: true,
        thClass: 'd-none',
        tdClass: 'd-none',
      },
      {
        key: 'origin_warehouse',
        label: 'Origin',
        sortable: true,
      },
      {
        key: 'destination_warehouse',
        label: 'Destination',
        sortable: true,
      },
      {
        key: 'customer_email',
        label: 'Customer Contact',
        sortable: true,
      },
      {
        key: 'cod',
        label: `COD (${this.currency_type})`,
        sortable: true,
        thClass: 'text-right',
        tdClass: 'text-right',
      },
      {
        key: 'collected_cod',
        label: `Collected Cod (${this.currency_type})`,
        sortable: true,
        thClass: 'text-right',
        tdClass: 'text-right',
      },
      {
        key: 'delivery_charge',
        label: `Delivery Charge (${this.currency_type})`,
        sortable: true,
        thClass: 'text-right',
        tdClass: 'text-right',
      },
      {
        key: 'weight',
        label: 'Weight',
        sortable: true,
        thClass: 'text-right',
        tdClass: 'text-right',
      },
      {
        key: 'status.key',
        label: 'Status',
        sortable: true,
      },
      {
        key: 'action',
        label: 'Action',
        sortable: false,
      },
    )
  },
  methods: {
    async getOrderDetails(waybill) {
      try {
        const { data } = (await orderRepository.getOrderDetails(waybill)).data
        if (data.waybill_number === waybill) {
          // await this.onSubmit()
        } else {
          this.showErrorMessage('Invalid waybill number!!')
        }
      } catch (e) {
        this.showErrorMessage('Invalid waybill number!!')
      }
    },
    async getOrderList() {
      this.dataLoading = true
      this.rows = []
      const filterQuery = this.buildFilterQuery()
      try {
        const { data } = (await orderRepository.getOrderListByKey(this.status_key, this.page, filterQuery, this.perPage))
        this.rows = data.data
        this.meta = data.meta
        this.submitCount = 0
        // eslint-disable-next-line no-empty
      } catch (e) {

      }
      this.dataLoading = false
    },
    async getOrderListNoPagination() {
      try {
        const filterQuery = this.buildFilterQuery()
        const { data } = (await orderRepository.getOrderListByKeyNoPagination(this.status_key, filterQuery))
        return data.data
        // eslint-disable-next-line no-empty
      } catch (e) {
        return []
      }
    },
    async onSubmit() {
      this.loadingResponse = true
      try {
        const payload = {
          waybill_numbers: [],
        }
        payload.waybill_numbers.push(this.formData.waybill_number)
        payload.status_key = this.status_key
        payload.other_details = {}

        await orderRepository.updateOrderStatus(payload)
        this.$refs.formbuilder.reset()
        this.loadingResponse = false
        // await this.getOrderList()
        this.showSuccessMessage('Order updated successfully')
        this.submitCount += 1
        // eslint-disable-next-line no-underscore-dangle
        this.__focusInputField('Waybill Number')
      } catch (e) {
        this.loadingResponse = false
        this.convertAndNotifyError(e)
      }
    },
    applyFilters() {
      this.getOrderList()
      this.onClickDeselectAll()
    },
    clearFilters() {
      this.filters = {
        'orders.created_at': {
          startDate: null,
          endDate: null,
        },
      }
      this.$nextTick(() => {
        this.getOrderList()
      })
    },
    onClickBulkAction() {
      this.$refs.bulkActionModal.openModal()
    },
    onClickSelectAll() {
      this.selectedWaybillArray = this.rows.map(n => n.waybill_number)
    },
    onClickDeselectAll() {
      this.selectedWaybillArray = []
    },
    changePage(value) {
      this.perPage = value
    },
    handleChangePage(page) {
      this.page = page
      this.getOrderList()
    },
    onClickViewOrder(id) {
      if (this.$can('show-orders')) {
        this.$refs.viewOrderModal.openModal(id)
      }
    },
    onClickRefresh() {
      this.getOrderList()
    },
    onClickSelectAllCheckbox() {
      if (this.selectAllCheckbox) {
        // If the "Select All" checkbox is checked, select all waybill numbers.
        this.selectedWaybillArray = this.rows.map(n => n.waybill_number)
      } else {
        this.selectedWaybillArray = []
      }
    },
    shouldShowSecondaryPhone(item) {
      // Check if secondary phone exists (works for both encrypted and non-encrypted)
      return item.customer_secondary_phone !== null && item.customer_secondary_phone !== undefined
    },
    getCustomerFieldValue(item, fieldName, defaultValue = null) {
      // If data is encrypted
      if (item.is_encrypted) {
        // If user has permission, API returns decrypted data - show it
        if (this.$can('show-decrypted-orders')) {
          return item[fieldName] || defaultValue || ''
        }
        // Otherwise show masked
        return '******'
      }
      // If not encrypted, show original value
      return item[fieldName] || defaultValue || ''
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../../@core/scss/button-color.scss';
</style>
